<template>
  <div id="bets">
    <v-data-table
      id="bets-table"
      :headers="sortedHeaders"
      :items="formattedBets"
      item-key="id"
      show-expand
      :sort-by="['date']"
      :sort-desc="[true]"
      :loading="loading"
      :items-per-page="-1"
      :search="filters.search"
      :item-class="rowClass"
      :footer-props="{
        itemsPerPageOptions: [20, 50, 100, -1]
      }"
      :custom-filter="filterAll"
    >
      <template v-slot:top>
        <v-container fluid pa-0>
          <v-row justify="start">
            <v-col cols="12" sm="6" md="6" lg="6" xl="6" class="py-0">
              <bet-filter
                v-bind:startDate.sync="filters.startDate"
                v-bind:endDate.sync="filters.endDate"
                v-bind:search.sync="filters.search"
                :refresh="refresh"
              />
            </v-col>
            <v-col cols="12" sm="6" md="6" lg="6" xl="6" class="py-0">
              <bet-summary
                v-if="!loading && bets.length > 0"
                :bets="bets"
              ></bet-summary>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <template v-slot:header.actions="{}">
        <div style="width: 20px">
          <v-select
            v-model="selectedHeaders"
            :items="allHeaders().filter(h => !h.locked)"
            multiple
            dense
            return-object
          >
            <template v-slot:selection="{}"> </template>
          </v-select>
        </div>
      </template>

      <template v-if="$vuetify.breakpoint.xs" v-slot:item="{ item }">
        <bet-mobile :bet="item"></bet-mobile>
      </template>

      <template v-slot:item.date="{ item }">
        <div :title="formatExtendedDate(item.date)">
          {{ formatDate(item.date) }}
        </div>
      </template>

      <template v-slot:item.race="{ item }">
        <span>{{ item.race }}</span>
      </template>

      <template v-slot:item.market_type="{ item }">
        <span>{{ formatMarketType(item.market_type) }}</span>
      </template>

      <template v-slot:item.placement_time="{ item }">
        {{ formatDuration(item.placement_time) }}
      </template>

      <template v-slot:item.processing_time="{ item }">
        {{ formatDuration(item.processing_time) }}
      </template>

      <template v-slot:item.stake="{ item }">
        {{ formatCurrency(item.stake) }}
      </template>

      <template v-slot:item.margin="{ item }">
        <span v-if="!item.final"></span>

        <span v-else>
          {{ formatPercentage(item.margin, 0) }}
        </span>
      </template>

      <template v-slot:item.back_odds="{ item }">
        <span>
          {{ formatCurrency(item.back_odds, 2) }}
        </span>
      </template>

      <template v-slot:item.liability="{ item }">
        <span>
          {{ formatCurrency(item.liability, 2) }}
        </span>
      </template>

      <template v-slot:item.mbl="{ item }">
        <span>
          {{ formatCurrency(item.mbl, 0) }}
        </span>
      </template>

      <template v-slot:item.bsp="{ item }">
        <span v-if="!item.final"></span>

        <span v-else>
          {{ formatCurrency(item.bsp, 2) }}
        </span>
      </template>

      <template v-slot:item.ev="{ item }">
        <span v-if="!item.final"></span>

        <span class="profit" v-else-if="item.ev > 0">
          {{ formatCurrency(item.ev, 2) }}
        </span>
        <span class="loss" v-else>
          {{ formatCurrency(item.ev, 2) }}
        </span>
      </template>

      <template v-slot:item.profit="{ item }">
        <span v-if="!item.final"></span>

        <span class="profit" v-else-if="item.profit > 0">
          {{ formatCurrency(item.profit, 2) }}
        </span>
        <span class="loss" v-else>
          {{ formatCurrency(item.profit, 2) }}
        </span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-icon small class="mr-2" v-bind="attrs" v-on="on">
              mdi-dots-horizontal
            </v-icon>
          </template>
          <v-list dense>
            <v-list-item @click="copyBetID(item.id)">
              <v-list-item-title>Copy Bet ID</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="showResultBetDialog(item)"
              v-if="item.outcome === 'ACCEPTED'"
            >
              <v-list-item-title>Result Bet</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="showConfirmBetDialog(item)"
              v-if="item.outcome === 'SUBMITTED'"
            >
              <v-list-item-title>Confirm Bet</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="showPlacementDetailsDialog(item)"
              v-if="item.outcome !== 'TRIGGERED'"
            >
              <v-list-item-title>Placement Details</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="requestScreenshots(item)"
              v-if="item.outcome !== 'TRIGGERED'"
            >
              <v-list-item-title>Request Screenshots</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="viewLogs(item)"
            >
              <v-list-item-title>View Logs</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :class="`elevation-0`" :colspan="headers.length">
          <bet-expanded :bet="item" :mobile="false"></bet-expanded>
        </td>
      </template>
    </v-data-table>
    <result-bet-dialog
      v-if="resulting.selectedBet"
      :bet="resulting.selectedBet"
      :dialog="resulting.showDialog"
      :close="closeResultBetDialog"
      :processing="resulting.processing"
      :confirm="submitResultBet"
    />
    <confirm-bet-dialog
      v-if="confirming.selectedBet"
      :bet="confirming.selectedBet"
      :dialog="confirming.showDialog"
      :close="closeConfirmBetDialog"
      :processing="confirming.processing"
      :confirm="submitConfirmBet"
    />
    <bet-placement-details-dialog
      v-if="placement.selectedBet"
      :betID="placement.selectedBet.id"
      :dialog="placement.showDialog"
      :close="closePlacementDetailsDialog"
    />
  </div>
</template>re

<script>
import moment from "moment";
import _ from "lodash";
import ResultBetDialog from "../components/ResultBetDialog";
import ConfirmBetDialog from "../components/ConfirmBetDialog";
import BetPlacementDetailsDialog from "../components/BetPlacementDetailsDialog";

import BetFilter from "../components/BetFilter";
import BetSummary from "../components/BetSummary";
import BetMobile from "../components/BetMobile";
import BetExpanded from "../components/BetExpanded";
import { listBets } from "../lib/bets";
import { resultBet, confirmBet, screenshotBet } from "../lib/commands";
import { duration, marketType, raceType } from "../lib/formatting";

const SEARCH_FIELDS = [
  "bookie",
  "bot",
  "race",
  "market",
  "outcome",
  "race_type",
  "market_type",
  "reject_reason",
  "selection",
  "reject_message",
  "id"
];

const termsRegex = /(".*?")|(\S+)/g;

function extractTerms(str) {
  let match;
  const terms = [];

  while ((match = termsRegex.exec(str)) !== null) {
    terms.push(match[1] ? match[1] : match[2]);
  }

  return terms;
}

const hasPartialMatch = (item, term) => {
  for (const field of SEARCH_FIELDS) {
    if (item[field] && item[field].toLowerCase().includes(term)) return true;
  }

  return false;
};

const hasExactMatch = (item, term) => {
  for (const field of SEARCH_FIELDS) {
    if (item[field] && item[field].toLowerCase() === term) return true;
  }

  return false;
};

export default {
  name: "Bets",
  components: {
    BetFilter,
    BetExpanded,
    BetMobile,
    BetSummary,
    ResultBetDialog,
    ConfirmBetDialog,
    BetPlacementDetailsDialog
  },
  data() {
    return {
      loading: true,
      showColumnSelector: false,
      filters: {
        search: "",
        startDate: moment().format("YYYY-MM-DD"),
        endDate: moment()
          .add(1, "day")
          .format("YYYY-MM-DD")
      },
      expanded: [],
      selectedHeaders: this.allHeaders().filter(h => h.visible !== false),
      bets: [],
      placement: {
        showDialog: false,
        selectedBet: null
      },
      resulting: {
        processing: false,
        showDialog: false,
        selectedBet: null
      },
      confirming: {
        processing: false,
        showDialog: false,
        selectedBet: null
      }
    };
  },
  computed: {
    sortedHeaders() {
      const headers = [...this.selectedHeaders];
      headers.sort((x, y) => {
        if (x.order < y.order) return -1;
        if (x.order > y.order) return 1;
        return 0;
      });
      return headers;
    },
    formattedBets() {
      const formatted = [];
      for (const b of this.bets) {
        const f = {
          id: b.id,
          bot: b.bot,
          date: b.dates.submitted || b.dates.triggered,
          placed: b.dates.placed,
          processing_time:
            moment(b.dates.placed).valueOf() -
            moment(b.dates.submitted).valueOf(),
          placement_time:
            moment(b.dates.submitted).valueOf() -
            moment(b.dates.triggered).valueOf(),
          triggered: b.dates.triggered,
          race_start_time: b.race.start_time,
          race: `${b.race.venue} R${b.race.number}`,
          race_venue: b.race.venue,
          race_state: b.race.venue_state,
          race_number: b.race.number,
          race_type: b.race.type,
          selection:
            b.market.type === "FAV_FIELD"
              ? b.selection.name
              : `${b.selection.number}. ${b.selection.name}`,
          market_type: b.market.type,
          bookie: b.bookie,
          stake: b.stake,
          final: b.state === "FINAL",
          outcome: _.get(b, "result.outcome", b.state),
          margin:
            b.state === "FINAL"
              ? 100 * (b.prices.bookie / b.prices.bsp) - 100
              : undefined,
          ev: _.get(b, "result.ev"),
          profit: 0,
          back_odds: b.prices.bookie,
          state: b.state,
          betfair_odds: b.prices.betfair,
          bsp: b.prices.bsp || 0,
          betfair_url: b.market.betfair_url,
          bookie_url: b.race.bookie_url,
          transaction_id: b.transaction_id,
          liability: b.stake * (b.prices.bookie - 1),
          submission_attempts: b.submission_attempts,
          mbl: b.mbl
        };

        if (b.reject_reason) {
          f.reject_reason = b.reject_reason;

          if (b.reject_message) {
            f.reject_message = b.reject_message;
          }
        }

        if (b.result) {
          f.profit = b.result.bet_return - b.stake;
        }

        if (f.bookie === "sportsbetting") {
          f.bookie = "boombet";
        }

        formatted.push(f);
      }

      return formatted;
    }
  },
  methods: {
    allHeaders() {
      const headers = [
        {
          text: "Date",
          align: "start",
          sortable: true,
          value: "date",
          locked: true
        },
        {
          text: "Bot",
          value: "bot",
          visible: true
        },
        {
          text: "Race",
          value: "race"
        },
        {
          text: "Bookie",
          value: "bookie"
        },
        {
          text: "Placement Time",
          value: "placement_time",
          visible: false
        },
        {
          text: "Processing Time",
          value: "processing_time",
          visible: false
        },
        {
          text: "Race Type",
          value: "race_type",
          visible: false
        },
        {
          text: "State",
          value: "race_state",
          visible: false
        },
        {
          text: "Market",
          value: "market_type"
        },
        {
          text: "Stake",
          value: "stake"
        },
        {
          text: "Submission Attempts",
          value: "submission_attempts",
          visible: false
        },
        {
          text: "Back Odds",
          value: "back_odds"
        },
        {
          text: "Liability",
          value: "liability",
          visible: false
        },
        {
          text: "MBL",
          value: "mbl",
          visible: false
        },
        {
          text: "BSP",
          value: "bsp"
        },
        {
          text: "Margin",
          value: "margin"
        },
        {
          text: "Outcome",
          value: "outcome"
        },
        {
          text: "EV",
          value: "ev"
        },
        {
          text: "Profit",
          value: "profit"
        },
        {
          value: "actions",
          sortable: false,
          locked: true
        }
      ];

      return headers.map((header, index) => ({
        ...header,
        order: index
      }));
    },
    filterAll(value, search, item) {
      if (search === null) return true;
      // remove this if we ever need to search for non-string fields
      if (typeof value !== "string") return false;

      const massaged = search.trim().toLowerCase();

      const terms = extractTerms(massaged);
      const exactTerms = terms
        .filter(t => t.startsWith('"') && t.endsWith('"'))
        .map(t => t.slice(1, -1));
      const partialTerms = terms.filter(
        t => !t.startsWith('"') || !t.endsWith('"')
      );

      // every term must partially match at least one search field
      for (const t of partialTerms) {
        if (!hasPartialMatch(item, t)) {
          return false;
        }
      }

      // every exact term must exactly match at least one search field
      for (const t of exactTerms) {
        if (!hasExactMatch(item, t)) {
          return false;
        }
      }

      return true;
    },
    showResultBetDialog(bet) {
      this.resulting.selectedBet = bet;
      this.resulting.showDialog = true;
    },
    showConfirmBetDialog(bet) {
      this.confirming.selectedBet = bet;
      this.confirming.showDialog = true;
    },
    showPlacementDetailsDialog(bet) {
      this.placement.selectedBet = bet;
      this.placement.showDialog = true;
    },
    closePlacementDetailsDialog() {
      this.placement.selectedBet = null;
      this.placement.showDialog = false;
    },
    closeResultBetDialog() {
      this.resulting.selectedBet = null;
      this.resulting.showDialog = false;
    },
    closeConfirmBetDialog() {
      this.confirming.selectedBet = null;
      this.confirming.showDialog = false;
    },
    viewLogs(item) {
      // 10 seconds before the bet was triggered
      const startTime = new Date(item.triggered).getTime() - 10 * 1000;
      // 1 hour after the bet was triggered
      const endTime = new Date(item.triggered).getTime() + 60 * 60 * 1000;

      const logURL = `https://slippy.grafana.net/explore?schemaVersion=1&panes=%7B%22dsd%22%3A%7B%22datasource%22%3A%22grafanacloud-logs%22%2C%22queries%22%3A%5B%7B%22refId%22%3A%22A%22%2C%22expr%22%3A%22%7Bapp%3D%5C%22slippy%5C%22%7D+%7C+json+%7C+bet_id+%3D+%60${item.id}%60%22%2C%22queryType%22%3A%22range%22%2C%22datasource%22%3A%7B%22type%22%3A%22loki%22%2C%22uid%22%3A%22grafanacloud-logs%22%7D%2C%22editorMode%22%3A%22builder%22%7D%5D%2C%22range%22%3A%7B%22from%22%3A%22${startTime}%22%2C%22to%22%3A%22${endTime}%22%7D%2C%22panelsState%22%3A%7B%22logs%22%3A%7B%22columns%22%3A%7B%220%22%3A%22Time%22%2C%221%22%3A%22Line%22%7D%2C%22visualisationType%22%3A%22logs%22%2C%22labelFieldName%22%3A%22labels%22%7D%7D%7D%7D&orgId=1`;
      window.open(logURL, "_blank");
    },
    async requestScreenshots(item) {
      try {
        const resp = await screenshotBet(
          item.bot,
          moment(item.date).format("YYYY-MM-DD"),
          item.bookie,
          item.id
        );
        if (resp.no_response) {
          this.showError("No response received from bot");
        } else {
          this.showSuccess("Screenshots sent");
        }
      } catch (err) {
        this.showError(err);
      }
    },
    traderRejected(item) {
      return (
        item.outcome === "REJECTED" &&
        item.reject_reason !== "MANUAL" &&
        item.processing_time > 5000
      );
    },
    async submitResultBet(bet, outcome, betReturn, bsp) {
      this.resulting.selectedBet = null;

      try {
        const resp = await resultBet(bet.bot, bet.id, outcome, betReturn, bsp);
        if (resp.no_response) {
          this.showError("No response received from bot");
        } else {
          this.showSuccess("Bet result sent to Slippy");
        }

        this.resulting.showDialog = false;
      } catch (err) {
        this.showError(err);
      } finally {
        this.resulting.processing = false;
      }
    },
    async submitConfirmBet(bet, outcome, acceptedStake, transactionID) {
      this.confirming.selectedBet = null;

      try {
        const resp = await confirmBet(
          bet.bot,
          bet.id,
          outcome,
          acceptedStake,
          transactionID
        );
        if (resp.no_response) {
          this.showError("No response received from bot");
        } else {
          this.showSuccess("Bet confirmation sent to Slippy");
        }

        this.confirming.showDialog = false;
      } catch (err) {
        this.showError(err);
      } finally {
        this.confirming.processing = false;
      }
    },

    textToClipboard(text) {
      var dummy = document.createElement("textarea");
      document.body.appendChild(dummy);
      dummy.value = text;
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);
    },
    copyBetID(id) {
      this.textToClipboard(id);
      this.showSuccess(`Copied bet ID ${id} to clipboard`);
    },
    formatDate(d) {
      return moment(d).format("ll LT");
    },
    formatExtendedDate(d) {
      return moment(d).format("h:mm:ss.SS A");
    },
    formatPercentage(value, decimals) {
      return value.toFixed(decimals) + "%";
    },
    formatDuration(ms) {
      return duration(ms);
    },
    formatCurrency(amount, decimals) {
      if (isNaN(amount) || amount === null || amount === undefined) return "";

      if (decimals === undefined) {
        return amount < 0 ? `- $${amount * -1}` : `$ ${amount}`;
      }

      return amount < 0
        ? `- $${(amount * -1).toFixed(decimals)}`
        : `$ ${amount.toFixed(decimals)}`;
    },
    rowClass(item) {
      if (this.traderRejected(item)) return "item-trader-rejected";
      if (item.outcome === "REJECTED") return "item-rejected";

      if (item.ev === undefined) return "";

      return item.ev < 0 ? "item-loss" : "item-profit";
    },
    async refresh() {
      this.loading = true;
      this.bets = [];
      try {
        this.bets = await listBets(
          moment(this.filters.startDate).toISOString(),
          moment(this.filters.endDate)
            .add(1, "day")
            .toISOString()
        );
      } catch (err) {
        this.showError(`Unable to read bets- ${err}`);
      }

      this.loading = false;
    },
    formatMarketType: marketType,
    formatRaceType: raceType
  },
  async mounted() {
    this.refresh();
  }
};
</script>

<style lang="scss">
#bets-table.v-data-table tbody tr.v-data-table__expanded__content {
  box-shadow: none;
}

span.profit {
  color: green;
}

span.loss {
  color: red;
}

tr.item-trader-rejected {
  background-color: rgba(255, 0, 0, 0.15);
}

tr.item-rejected {
  background-color: rgba(255, 165, 0, 0.15);
}
</style>
